
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = "support@taptotable.co";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const FIRST_TOUCH_KEY = "ttt_attribution_first_touch_v1";
const LAST_TOUCH_KEY = "ttt_attribution_last_touch_v1";

const ATTRIBUTION_FIELDS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content",
  "utm_term", "fbclid", "campaign_id", "adset_id", "ad_id",
];

function readStoredAttribution(key) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "null");
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

function loadAndRefreshAttribution() {
  if (typeof window === "undefined") {
    return { first_touch: null, last_touch: null };
  }

  const first = readStoredAttribution(FIRST_TOUCH_KEY);
  const last = readStoredAttribution(LAST_TOUCH_KEY);
  const params = new URLSearchParams(window.location.search);
  const values = {};

  ATTRIBUTION_FIELDS.forEach((field) => {
    const value = (params.get(field) || "").trim();
    if (value) values[field] = value;
  });

  if (Object.keys(values).length === 0) {
    return { first_touch: first, last_touch: last };
  }

  if (!values.utm_source && values.fbclid) {
    values.utm_source = "meta";
  }
  if (!values.utm_medium && values.fbclid) {
    values.utm_medium = "paid_social";
  }

  const current = {
    ...values,
    landing_url: window.location.href,
    timestamp: new Date().toISOString(),
  };

  try {
    if (!first) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(current));
    }
    window.localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(current));
  } catch {
    // Browser storage must never block signup.
  }

  return {
    first_touch: first || current,
    last_touch: current,
  };
}

const perks = [
  "Your TapToTable Page built before activation",
  "Review your page and request changes",
  "Your hosted TapToTable Page",
  "Secure, Easy-to-Use TapToTable Dashboard",
  "Create and update Promotions, Events, and Featured Items anytime",
  "Update restaurant details, photos, links, and customer actions",
  "Customer activity and supported analytics",
  "Restaurant photo hosting + TapToTable hosting",
  "Ongoing support",
  "Cancel anytime",
];

const pageSources = [
  "Instagram",
  "Restaurant website",
  "Google Business Profile",
  "Other restaurant or social page",
];

const OFFER_OPTIONS = [
  "Limited-Time Special",
  "Free Add-On With Purchase",
  "Loyalty Reward",
  "Seasonal Promotion",
  "Happy Hour",
  "Other / Custom",
];

const EVENT_OPTIONS = [
  "Live Music",
  "Trivia Night",
  "Watch Party",
  "Tasting",
  "Holiday Event",
  "Special Dinner",
  "Grand Opening",
  "Guest List / RSVP",
  "Ticketed Event",
  "Other / Custom",
];

const EVENT_HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const EVENT_MINUTES = ["00", "15", "30", "45"];
const EVENT_MERIDIEMS = ["AM", "PM"];

const inputStyle =
  "w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]";
const violetInputStyle =
  "w-full rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400";

function parseEventTime(value = "") {
  const fallback = {
    startHour: "6",
    startMinute: "00",
    startMeridiem: "PM",
    endHour: "7",
    endMinute: "00",
    endMeridiem: "PM",
  };

  const match = String(value).match(
    /(\d{1,2}):(\d{2})\s*(AM|PM)\s*[–—-]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i
  );

  if (!match) return fallback;

  return {
    startHour: String(Number(match[1]) || 6),
    startMinute: match[2],
    startMeridiem: match[3].toUpperCase(),
    endHour: String(Number(match[4]) || 7),
    endMinute: match[5],
    endMeridiem: match[6].toUpperCase(),
  };
}

function updateEventTime(value, key, nextValue) {
  const p = { ...parseEventTime(value), [key]: nextValue };
  return `${p.startHour}:${p.startMinute} ${p.startMeridiem} – ${p.endHour}:${p.endMinute} ${p.endMeridiem}`;
}

function formatDateValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseDateValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function SignupEventDatePicker({ value, onChange }) {
  const selected = parseDateValue(value);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = selected || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: days }, (_, i) =>
      new Date(month.getFullYear(), month.getMonth(), i + 1)
    ),
  ];

  const choose = (date) => {
    onChange(formatDateValue(date));
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="input-event-date"
        onClick={() => setOpen(!open)}
        className={`${violetInputStyle} flex items-center justify-between text-left`}
      >
        <span className={value ? "text-white" : "text-zinc-500"}>
          {selected
            ? selected.toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "Choose event date"}
        </span>
        <span className="text-violet-300">▦</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-[300px] max-w-[calc(100vw-4rem)] rounded-2xl border border-violet-400/25 bg-[#18181C] p-4 shadow-2xl">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() =>
                setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
              }
              className="rounded-lg border border-white/10 px-3 py-2"
              aria-label="Previous month"
            >
              ←
            </button>
            <span className="text-sm font-semibold">
              {month.toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              type="button"
              onClick={() =>
                setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
              }
              className="rounded-lg border border-white/10 px-3 py-2"
              aria-label="Next month"
            >
              →
            </button>
          </div>

          <div className="mt-4 grid grid-cols-7 gap-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div key={day} className="py-1 text-center text-[10px] text-zinc-500">
                {day}
              </div>
            ))}
            {cells.map((date, i) =>
              date ? (
                <button
                  key={formatDateValue(date)}
                  type="button"
                  onClick={() => choose(date)}
                  className={`h-9 rounded-lg text-sm ${
                    value === formatDateValue(date)
                      ? "bg-violet-400 text-black"
                      : "text-zinc-300 hover:bg-white/10"
                  }`}
                >
                  {date.getDate()}
                </button>
              ) : (
                <div key={`empty-${i}`} />
              )
            )}
          </div>

          <div className="mt-4 flex justify-between border-t border-white/10 pt-3">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="text-xs text-zinc-400"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
                choose(today);
              }}
              className="rounded-full bg-violet-400/15 px-4 py-2 text-xs text-violet-200"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  testid,
  optional = false,
  required = false,
}) {
  return (
    <div className="mb-5">
      <label className="label-caps !text-[10px] !tracking-[0.18em] block mb-2">
        {label}{" "}
        {required && (
          <span className="ml-1 text-[#00E5FF] normal-case tracking-normal">
            (required)
          </span>
        )}
        {optional && (
          <span className="text-zinc-600 normal-case tracking-normal">
            (optional)
          </span>
        )}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${inputStyle} ${
          error ? "!border-red-500/60" : ""
        }`}
        data-testid={testid}
      />
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
}

function EventTimeControls({ value, onChange }) {
  const parts = parseEventTime(value);

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {[
          ["Starts", "startHour", "startMinute", "startMeridiem"],
          ["Ends", "endHour", "endMinute", "endMeridiem"],
        ].map(([label, hourKey, minuteKey, meridiemKey]) => (
          <div
            key={label}
            className="rounded-2xl border border-white/10 bg-[#121214] p-4"
          >
            <p className="mb-3 text-xs font-semibold uppercase text-violet-300">
              {label}
            </p>
            <div className="grid grid-cols-[1fr_1.15fr_1fr] gap-2">
              {[
                [hourKey, EVENT_HOURS],
                [minuteKey, EVENT_MINUTES],
                [meridiemKey, EVENT_MERIDIEMS],
              ].map(([key, options]) => (
                <select
                  key={key}
                  value={parts[key]}
                  onChange={(e) =>
                    onChange(updateEventTime(value, key, e.target.value))
                  }
                  className="min-h-[50px] min-w-0 w-full rounded-xl border border-zinc-700 bg-zinc-900 px-2 py-3 text-center text-sm font-semibold text-white [color-scheme:dark]"
                >
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {key === minuteKey ? `:${option}` : option}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>
        ))}
      </div>
      {value && <p className="mt-2 text-xs text-zinc-500">{value}</p>}
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    restaurant_name: "",
    instagram_handle: "",
    email: "",
    campaign_type: "offer",
    offer: "",
    event_name: "",
    event_date: "",
    event_time: "",
    event_description: "",
    featured_item_name: "",
    featured_item_description: "",
    additional_locations_count: 0,
    image_url: "",
    menu_url: "",
    website: "",
    form_started_at: new Date().toISOString(),
  });

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [offerChoice, setOfferChoice] = useState("");
  const [eventChoice, setEventChoice] = useState("");
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [attribution, setAttribution] = useState({
    first_touch: null,
    last_touch: null,
  });

  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  useEffect(() => {
    setAttribution(loadAndRefreshAttribution());
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;

    const renderTurnstile = () => {
      if (
        cancelled ||
        !turnstileContainerRef.current ||
        !window.turnstile ||
        turnstileWidgetIdRef.current !== null
      ) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          callback: (token) => setTurnstileToken(token || ""),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
        }
      );
    };

    const existingScript = document.querySelector(
      'script[data-taptotable-turnstile="true"]'
    );

    if (existingScript) {
      if (window.turnstile) {
        renderTurnstile();
      } else {
        existingScript.addEventListener("load", renderTurnstile, {
          once: true,
        });
      }
    } else {
      const script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.taptotableTurnstile = "true";
      script.addEventListener("load", renderTurnstile, {
        once: true,
      });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken("");

    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      } catch {
        // Widget may already be removed.
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = {};

    if (!form.restaurant_name.trim()) {
      next.restaurant_name = "Required";
    }

    if (!form.instagram_handle.trim()) {
      next.instagram_handle =
        "Enter an Instagram handle, restaurant website, Google Business Profile, or social page";
    }

    if (!form.email.trim()) {
      next.email = "Required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      next.email = "Enter a valid email";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    if (!validate()) return;

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Please complete the quick security check.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await axios.post(`${API}/leads`, {
        ...form,
        attribution,
        turnstile_token: turnstileToken,
      });

      toast.success("TapToTable Page request received!");

      navigate("/thanks", {
        state: {
          slug: res.data?.slug,
          restaurant_name: res.data?.restaurant_name,
        },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : "Could not submit. Please try again.";
      toast.error(message);
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  const setCampaign = (campaign_type) => {
    setForm((prev) => ({
      ...prev,
      campaign_type,
      event_time:
        campaign_type === "event"
          ? prev.event_time || "6:00 PM – 7:00 PM"
          : prev.event_time,
    }));
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="signup-page">
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <div className="mx-auto grid max-w-6xl items-start gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-28 lg:col-span-5"
          >
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white"
              data-testid="signup-back-link"
            >
              ← Back to home
            </Link>

            <div className="label-caps">Let&apos;s Build Your Page</div>

            <h1
              className="mt-3 font-display text-3xl font-semibold tracking-[-0.01em] sm:text-4xl lg:text-5xl"
              data-testid="signup-heading"
            >
              Let&apos;s Build Your TapToTable Page
            </h1>

            <p className="mt-5 text-base leading-relaxed text-zinc-400 md:text-lg">
              Tell us about your restaurant and we&apos;ll build your
              TapToTable Page. You&apos;ll receive a private review link,
              can request changes, and only activate when you&apos;re satisfied.
            </p>

            <div className="mt-6 inline-flex flex-wrap items-center rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-3 text-sm text-zinc-100">
              <strong className="text-white">We Build It</strong>
              <span className="mx-2 text-zinc-400">→</span>
              <strong className="text-white">You Review It</strong>
              <span className="mx-2 text-zinc-400">→</span>
              <span>Activate for $59/month</span>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-zinc-300">
              Your first restaurant location is $59/month, including a
              secure, easy-to-use TapToTable Dashboard, hosting, supported
              customer activity analytics, and ongoing support.
            </p>

            <p className="mt-3 text-sm leading-relaxed text-zinc-300">
              Additional restaurant locations under the same ownership
              are $39/month each. Every location has its own page,
              login, and dashboard.
            </p>

            <ul className="mt-8 space-y-3">
              {perks.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm text-zinc-200">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#00E5FF]/15 text-[#00E5FF]">
                    ✓
                  </span>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="label-caps !text-[10px] !tracking-[0.18em]">
                We Can Build Your Page Using
              </p>

              <ul className="mt-4 space-y-3">
                {pageSources.map((source) => (
                  <li key={source} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span className="mt-0.5 text-[#00E5FF]">✓</span>
                    <span>{source}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                Provide your best restaurant link or Instagram handle.
                We&apos;ll use the available information to start building.
              </p>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            onSubmit={handleSubmit}
            className="rounded-2xl border border-white/5 bg-[#121214] p-6 md:p-10 lg:col-span-7"
            data-testid="signup-form"
            noValidate
          >
            <div
              aria-hidden="true"
              className="absolute left-[-10000px] top-auto h-px w-px overflow-hidden"
            >
              <label htmlFor="signup-website">Website</label>
              <input
                id="signup-website"
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>

            <div className="rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/[0.05] p-5 md:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full bg-[#00E5FF] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-black">
                  Step 1 · Required
                </div>
                <div className="text-xs font-semibold text-cyan-200">
                  Just 3 things to get started
                </div>
              </div>

              <h2 className="mt-4 font-display text-2xl font-semibold sm:text-3xl">
                Start Your TapToTable Page
              </h2>

              <p className="mb-6 mt-2 text-sm leading-relaxed text-zinc-400">
                That&apos;s enough for us to begin. Everything in Step 2
                is optional.
              </p>

              <Field
                label="Restaurant Name"
                name="restaurant_name"
                value={form.restaurant_name}
                onChange={handleChange}
                placeholder="Ember & Stone Pizzeria"
                error={errors.restaurant_name}
                testid="input-restaurant-name"
                required
              />

              <Field
                label="Instagram Handle, Restaurant Website, Google Business Profile, or Social Page"
                name="instagram_handle"
                value={form.instagram_handle}
                onChange={handleChange}
                placeholder="https://emberandstone.com or @emberandstonepizzeria"
                error={errors.instagram_handle}
                testid="input-instagram-handle"
                required
              />

              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="owner@emberandstone.com"
                error={errors.email}
                testid="input-email"
                required
              />
            </div>

            <div className="mt-8 rounded-3xl border border-violet-400/25 bg-violet-400/[0.045] p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-violet-400/40 bg-violet-400/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-violet-200">
                  Step 2 · Optional
                </div>
                <div className="text-xs font-semibold text-violet-200">
                  Skip this entire section if you want
                </div>
              </div>

              <h3 className="mt-4 font-display text-2xl font-semibold sm:text-3xl">
                Help Us Build It Faster
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Choose what you&apos;d like to feature first. We can find
                most restaurant information and refine the page before activation.
              </p>

              <div className="mt-6">
                <label className="label-caps !text-[10px] !tracking-[0.18em] mb-3 block">
                  What Would You Like to Feature First?
                </label>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    {
                      value: "offer",
                      title: "Promotion / Offer",
                      text: "Offer a special or incentive customers can claim.",
                      color: "border-[#00E5FF] bg-[#00E5FF]/10",
                    },
                    {
                      value: "event",
                      title: "Event",
                      text: "Promote live music, trivia, tastings, or other events.",
                      color: "border-violet-400 bg-violet-400/10",
                    },
                    {
                      value: "featured_item",
                      title: "Featured Item",
                      text: "Spotlight a signature dish without discounts or claim codes.",
                      color: "border-amber-400 bg-amber-400/10",
                    },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setCampaign(option.value)}
                      className={`rounded-2xl border px-3 py-4 text-left transition-all ${
                        form.campaign_type === option.value
                          ? option.color
                          : "border-white/10 bg-[#1A1A1F] hover:border-white/20"
                      }`}
                      data-testid={`signup-campaign-${option.value}`}
                    >
                      <div className="text-sm font-semibold text-white">
                        {option.title}
                      </div>
                      <div className="mt-1 text-xs leading-relaxed text-zinc-500">
                        {option.text}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {form.campaign_type === "event" ? (
                <div className="mt-6 rounded-2xl border border-violet-400/20 bg-black/20 p-4 sm:p-5">
                  <div className="mb-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                      Event Setup
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      Pick an event type or choose Custom.
                    </p>
                  </div>

                  <div className="mb-5">
                    <label className="label-caps !text-[10px] mb-2 block">
                      Event Type
                    </label>
                    <select
                      value={eventChoice}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEventChoice(value);
                        setForm((prev) => ({
                          ...prev,
                          event_name: value === "Other / Custom" ? "" : value,
                        }));
                      }}
                      className={violetInputStyle}
                      data-testid="signup-event-type"
                    >
                      <option value="">Choose an event...</option>
                      {EVENT_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {eventChoice === "Other / Custom" && (
                    <Field
                      label="Custom Event"
                      name="event_name"
                      value={form.event_name}
                      onChange={handleChange}
                      placeholder="e.g. Summer Patio Party"
                      testid="input-event-name"
                      optional
                    />
                  )}

                  <div className="mb-5">
                    <label className="label-caps !text-[10px] mb-2 block">
                      Event Date (optional)
                    </label>
                    <SignupEventDatePicker
                      value={form.event_date}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, event_date: value }))
                      }
                    />
                  </div>

                  <div className="mb-5">
                    <label className="label-caps !text-[10px] mb-2 block">
                      Event Time (optional)
                    </label>
                    <EventTimeControls
                      value={form.event_time}
                      onChange={(value) =>
                        setForm((prev) => ({ ...prev, event_time: value }))
                      }
                    />
                  </div>

                  <div>
                    <label className="label-caps !text-[10px] mb-2 block">
                      Anything Else About the Event? (optional)
                    </label>
                    <textarea
                      name="event_description"
                      value={form.event_description}
                      onChange={handleChange}
                      placeholder="Add anything customers should know."
                      rows={3}
                      className={violetInputStyle}
                      data-testid="input-event-description"
                    />
                  </div>
                </div>
              ) : form.campaign_type === "featured_item" ? (
                <div className="mt-6 rounded-2xl border border-amber-400/20 bg-black/20 p-4 sm:p-5">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
                    Featured Item Setup
                  </div>
                  <p className="mb-5 mt-2 text-xs leading-relaxed text-zinc-500">
                    Showcase a signature menu item, popular dish, or new favorite.
                    No discount or redemption code is required.
                  </p>

                  <Field
                    label="Featured Menu Item Name"
                    name="featured_item_name"
                    value={form.featured_item_name}
                    onChange={handleChange}
                    placeholder="e.g. Signature Bacon Cheeseburger"
                    testid="input-featured-item-name"
                    optional
                  />

                  <label className="label-caps !text-[10px] mb-2 block">
                    Featured Item Description (optional)
                  </label>
                  <textarea
                    name="featured_item_description"
                    value={form.featured_item_description}
                    onChange={handleChange}
                    placeholder="Describe the ingredients, flavor, or what makes it special."
                    rows={3}
                    className={inputStyle}
                    data-testid="input-featured-item-description"
                  />
                  <p className="mt-3 text-xs text-zinc-500">
                    You can provide a photo and menu link below, or send
                    them to us later.
                  </p>
                </div>
              ) : (
                <div className="mt-6 rounded-2xl border border-[#00E5FF]/20 bg-black/20 p-4 sm:p-5">
                  <div className="mb-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                      Promotion / Offer Setup
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      Choose an offer idea or create your own.
                    </p>
                  </div>

                  <label className="label-caps !text-[10px] mb-2 block">
                    Offer Type
                  </label>
                  <select
                    value={offerChoice}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOfferChoice(value);
                      setForm((prev) => ({
                        ...prev,
                        offer: value === "Other / Custom" ? "" : value,
                      }));
                    }}
                    className={inputStyle}
                    data-testid="signup-offer-type"
                  >
                    <option value="">Choose an offer idea...</option>
                    {OFFER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  {offerChoice === "Other / Custom" && (
                    <div className="mt-4">
                      <label className="label-caps !text-[10px] mb-2 block">
                        Custom Promotion / Offer (optional)
                      </label>
                      <textarea
                        name="offer"
                        value={form.offer}
                        onChange={handleChange}
                        placeholder="e.g. Free garlic knots with any large pizza"
                        rows={3}
                        className={inputStyle}
                        data-testid="input-offer"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5">
                <label className="label-caps !text-[10px] mb-2 block">
                  Additional Restaurant Locations (optional)
                </label>

                <select
                  value={form.additional_locations_count}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      additional_locations_count: Number(e.target.value),
                    }))
                  }
                  className={inputStyle}
                  data-testid="signup-additional-locations"
                >
                  <option value={0}>Just this restaurant</option>
                  {[1, 2, 3, 4, 5, 10].map((count) => (
                    <option key={count} value={count}>
                      {count} additional {count === 1 ? "location" : "locations"}
                    </option>
                  ))}
                </select>

                <p className="mt-3 text-xs leading-relaxed text-zinc-500">
                  First location: $59/month. Each additional location
                  under the same ownership: $39/month. We&apos;ll contact
                  you to arrange separate pages, logins, and dashboards.
                </p>
              </div>

              <div className="mt-6 border-t border-white/10 pt-5">
                <button
                  type="button"
                  onClick={() => setShowMoreInfo((current) => !current)}
                  className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left hover:bg-white/[0.06]"
                  data-testid="signup-more-info-toggle"
                >
                  <span>
                    <span className="block text-sm font-semibold">
                      Have More Info? Add It Here
                    </span>
                    <span className="mt-0.5 block text-xs text-zinc-500">
                      Photo and menu links are completely optional.
                    </span>
                  </span>
                  <span className="ml-3 text-lg text-zinc-400">
                    {showMoreInfo ? "−" : "+"}
                  </span>
                </button>

                {showMoreInfo && (
                  <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="mb-5">
                      <label className="label-caps !text-[10px] mb-2 block">
                        Featured Photo URL (optional)
                      </label>
                      <input
                        type="url"
                        name="image_url"
                        value={form.image_url}
                        onChange={handleChange}
                        placeholder="https://your-photo-url.jpg"
                        className={inputStyle}
                        data-testid="input-image-url"
                      />
                      <p className="mt-2 text-xs text-zinc-500">
                        Or email photos to{" "}
                        <a
                          href={`mailto:${SUPPORT_EMAIL}`}
                          className="text-[#00E5FF] hover:underline"
                        >
                          {SUPPORT_EMAIL}
                        </a>
                        .
                      </p>
                    </div>

                    <label className="label-caps !text-[10px] mb-2 block">
                      Menu URL (optional)
                    </label>
                    <input
                      type="url"
                      name="menu_url"
                      value={form.menu_url}
                      onChange={handleChange}
                      placeholder="https://your-menu-link.com"
                      className={inputStyle}
                      data-testid="input-menu-url"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/10 px-4 py-4 text-center text-sm font-medium text-zinc-100">
              <div>No payment today. Review your completed page first.</div>
              <div className="mt-1 text-xs font-normal text-zinc-300">
                First location $59/month; eligible additional locations
                $39/month each. Activate only when ready.
              </div>
            </div>

            {TURNSTILE_SITE_KEY && (
              <div className="mt-5 flex justify-center">
                <div
                  ref={turnstileContainerRef}
                  data-testid="signup-turnstile"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#00E5FF] px-6 py-4 font-semibold text-black transition-all hover:bg-[#66FFFF] disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="signup-submit-button"
            >
              {submitting ? "Submitting..." : "Build My TapToTable Page"}
            </button>

            <p className="mt-4 text-center text-xs leading-relaxed text-zinc-500">
              By submitting this form, you agree to the{" "}
              <Link to="/terms" className="text-[#00E5FF] hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#00E5FF] hover:underline">
                Privacy Policy
              </Link>
              .
            </p>

            <p className="mt-3 text-center text-xs leading-relaxed text-zinc-500">
              We&apos;ll build your page, send a private review link,
              and make requested changes before activation.
            </p>
          </motion.form>
        </div>
      </main>

      <Footer />
    </div>
  );
}
